import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth";
import { Button } from "@shared-frontend/components/ui/button";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@shared-frontend/components/ui/card";
import {
    Alert,
    AlertDescription,
} from "@shared-frontend/components/ui/alert";
import { AlertCircle, Building2, ChevronRight } from "lucide-react";

/**
 * Tenant selection page for users with access to multiple tenants/workspaces
 */
function TenantSelectorPage() {
    const navigate = useNavigate();
    const {
        availableTenants,
        selectTenant,
        isLoading,
        error,
        pendingTenantSelection,
        isAuthenticated,
    } = useAuth();
    const [selectedTenant, setSelectedTenant] = useState<string | null>(null);

    // If not pending tenant selection, redirect appropriately
    if (!pendingTenantSelection) {
        if (isAuthenticated) {
            return <Navigate to="/" replace />;
        }
        return <Navigate to="/login" replace />;
    }

    // If no tenants available, redirect to login
    if (!availableTenants || availableTenants.length === 0) {
        return <Navigate to="/login?error=no_tenants" replace />;
    }

    async function handleSelectTenant(tenantId: string) {
        setSelectedTenant(tenantId);
        try {
            await selectTenant(tenantId);
            navigate("/", { replace: true });
        } catch {
            setSelectedTenant(null);
        }
    }

    return (
        <div className="flex min-h-screen w-full items-center justify-center p-6">
            <Card className="w-full max-w-[500px]">
                <CardHeader className="space-y-3">
                    <div className="flex flex-col items-center space-y-4">
                        <CardTitle className="text-2xl font-semibold">
                            Select Workspace
                        </CardTitle>
                        <CardDescription className="text-center text-sm text-muted-foreground">
                            You have access to multiple workspaces. Please select one to continue.
                        </CardDescription>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col space-y-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            {availableTenants.map((tenant) => (
                                <Button
                                    key={tenant.id}
                                    variant="outline"
                                    className="w-full justify-between h-auto py-4 px-4"
                                    onClick={() => handleSelectTenant(tenant.id)}
                                    disabled={isLoading}
                                >
                                    <div className="flex items-center gap-3">
                                        <Building2 className="h-5 w-5 text-muted-foreground" />
                                        <span className="font-medium">{tenant.name}</span>
                                    </div>
                                    {selectedTenant === tenant.id && isLoading ? (
                                        <span className="text-sm text-muted-foreground">
                                            Loading...
                                        </span>
                                    ) : (
                                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                    )}
                                </Button>
                            ))}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default TenantSelectorPage;
